import { useState } from 'react'
import { Navigate } from 'react-router'
import { useAnyPermission } from '@/hooks/usePermission'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PageHeader, PermissionGate } from '@/components/common'
import { ExpiringDocumentsCard } from '@/features/document'
import { useCompanySettings } from '@/hooks/useCompanySettings'
import { downloadFile, toApiProblem } from '@/lib/api/client'
import { DatePicker } from '@/components/ui/date-picker'
import {
  StatCardsRow, AttendanceOverviewCard, EmployeeTrendCard, UpcomingEventsCard,
  SummaryCardsRow, QuickAccessCard, DepartmentHeadcountCard, RecentActivitiesCard,
} from '../components'

/**
 * The HR Admin dashboard, laid out to match design/dashboard.png.
 *
 * The grid is 12 columns on xl and collapses to a single column on mobile. Row spans
 * follow the design: the donut takes 5, the trend 4 and events 3; the four summary cards
 * split evenly; headcount takes 8 against the activity feed's 4.
 *
 * Each widget owns its own query, so a card the user lacks permission for — or one whose
 * module has not shipped yet — degrades on its own without taking the page with it.
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const { settings } = useCompanySettings()
  const [exporting, setExporting] = useState(false)

  const [exportError, setExportError] = useState<string | null>(null)
  // Every widget here is the company's figures, behind the read-all that owns them. Somebody
  // holding none of those — an employee — has nothing to see on this page, and their own
  // figures are on My workspace, so that is where '/' takes them.
  const seesCompanyFigures = useAnyPermission(
    ['employee:read-all', 'attendance:read-all', 'leave:read-all', 'payroll:read-all'])

  /**
   * The dashboard as a spreadsheet.
   *
   * <p>XLSX rather than PDF: the figures on this screen are counts somebody wants to sort,
   * total and paste into a board pack, and a PDF of the same tables is a picture of them.
   */
  async function downloadReport() {
    setExporting(true)
    setExportError(null)
    try {
      await downloadFile('/v1/dashboard/report', `dashboard-${date}.xlsx`, { date })
    } catch (caught) {
      setExportError(toApiProblem(caught)?.detail ?? 'The report could not be produced')
    } finally {
      setExporting(false)
    }
  }

  if (!seesCompanyFigures) {
    return <Navigate to="/portal" replace />
  }

  return (
    <div className="space-y-6">
      {/* The page owns its title. The header above carries the product's name, not this. */}
      <PageHeader
        title={t('nav.dashboard')}
        actions={
          <>
            {/* One icon: the picker draws its own, so the overlaid Calendar that used to
                sit here — alongside the native control's own button — is gone. */}
            <DatePicker
              aria-label="Dashboard date"
              value={date}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(event) => setDate(event.target.value)}
              className="w-44"
            />

            <Button onClick={() => void downloadReport()} disabled={exporting}>
              <Download aria-hidden="true" />
              {exporting ? 'Preparing...' : 'Download Report'}
            </Button>
          </>
        }
      />

      {exportError && (
        <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {exportError}
        </p>
      )}

      {/* Row 1 — five stat cards */}
      <StatCardsRow date={date} />

      {/*
       * Rows are 24px apart and their cards stretch to the tallest in the row: a short card
       * beside a tall one used to leave a ragged gap under it, which read as a card that had
       * failed to load the rest of its content.
       */}
      {/* Row 2 — attendance donut (5) · employee trend (4) · upcoming events (3) */}
      <div data-dashboard-row className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-12">
        <div className="xl:col-span-5 [&>*]:h-full">
          <AttendanceOverviewCard date={date} />
        </div>
        <div className="xl:col-span-4 [&>*]:h-full">
          <EmployeeTrendCard />
        </div>
        <div className="xl:col-span-3 [&>*]:h-full">
          <UpcomingEventsCard />
        </div>
      </div>

      {/* Row 3 — leave · payroll · recruitment · quick access */}
      <div data-dashboard-row className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCardsRow />
        <QuickAccessCard />
      </div>

      {/* Row 4 — department headcount (8) · recent activities (4) */}
      <div data-dashboard-row className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8 [&>*]:h-full">
          <DepartmentHeadcountCard />
        </div>
        <div className="xl:col-span-4 [&>*]:h-full">
          <RecentActivitiesCard />
        </div>
      </div>

      {/* Row 5 — what is about to run out. Behind document:read: a trade licence expiring
          is not something everyone with a dashboard should be shown. */}
      <PermissionGate permission="document:read">
        <div data-dashboard-row className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-12">
          <div className="xl:col-span-6 [&>*]:h-full">
            <ExpiringDocumentsCard />
          </div>
        </div>
      </PermissionGate>

      {/* Currency and date format come from company settings, never hardcoded. */}
      <p className="sr-only">
        {settings
          ? `Figures shown in ${settings.defaultCurrency}, dated ${format(new Date(date), settings.dateFormat)}.`
          : 'The company\'s currency and date format could not be loaded, so figures are shown '
            + 'without a currency symbol and dates in ISO form.'}
      </p>
    </div>
  )
}
